import React, { useState, useEffect } from 'react';

const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', className = '' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    vehicles: ['M1 3h15v13H1z', 'M16 8l4 4v5h-4z', 'M5 18a2 2 0 100 4 2 2 0 000-4z', 'M12 18a2 2 0 100 4 2 2 0 000-4z'],
    operators: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 11a4 4 0 100-8 4 4 0 000 8z'],
    routes: ['M1 6l7-3 8 3 7-3v15l-7 3-8-3-7 3z', 'M8 3v15', 'M16 6v15'],
    users: ['M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2', 'M9 7a4 4 0 100-8 4 4 0 000 8z', 'M22 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
    price: ['M2 6h20v12H2z', 'M12 12a3 3 0 100-6 3 3 0 000 6z', 'M6 12h.01', 'M18 12h.01'],
    check: 'M20 6L9 17l-5-5',
    card: ['M1 4h22v16H1z', 'M1 10h22'],
};

const defaultForm = {
    name: '',
    description: '',
    isFree: false,
    price: '',
    prices: [],
    pricePeriod: '',
    isAllCountries: true,
    targetCountries: [],
    fleetLimit: -1,
    driversLimit: -1,
    routesLimit: -1,
    usersLimit: -1,
    passengerLimit: -1,
    clientsLimit: -1,
    isActive: true,
};

export default function CreatePricePlan({ editPlan, onSaved, user }) {
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [countries, setCountries] = useState([]);
    const [billingPeriods, setBillingPeriods] = useState([]);

    useEffect(() => {
        Promise.all([
            fetch('/api/countries').then(res => res.json()),
            fetch('/api/master-data').then(res => res.json())
        ])
        .then(([countriesData, masterData]) => {
            setCountries((Array.isArray(countriesData) ? countriesData : []).filter(c => c.isActive));
            const mData = Array.isArray(masterData) ? masterData : [];
            setBillingPeriods(mData.filter(m => m.category === 'billing-periods' && m.active !== false));
        })
        .catch(err => console.error("Error fetching setup data:", err));
    }, []);

    useEffect(() => {
        if (editPlan) {
            setForm({
                name: editPlan.name || '',
                description: editPlan.description || '',
                isFree: editPlan.isFree || false,
                price: editPlan.price ?? '',
                prices: editPlan.prices || [],
                pricePeriod: editPlan.pricePeriod || '',
                isAllCountries: editPlan.isAllCountries ?? true,
                targetCountries: editPlan.targetCountries || [],
                fleetLimit: editPlan.fleetLimit ?? -1,
                driversLimit: editPlan.driversLimit ?? -1,
                routesLimit: editPlan.routesLimit ?? -1,
                usersLimit: editPlan.usersLimit ?? -1,
                passengerLimit: editPlan.passengerLimit ?? -1,
                clientsLimit: editPlan.clientsLimit ?? -1,
                isActive: editPlan.isActive ?? true,
            });
        } else {
            setForm(defaultForm);
        }
    }, [editPlan]);

    const isEditMode = !!editPlan;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'price') {
            const numVal = Number(value);
            const newPrices = countries.map(c => ({
                country: c.name,
                currency: c.currency || '',
                amount: numVal * (c.exchangeRate || c.rate || 1)
            }));
            setForm(f => ({ ...f, price: value, prices: newPrices }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    const handlePriceChange = (countryName, value) => {
        const numVal = value === '' ? '' : Number(value);
        setForm(f => {
            const newPrices = [...f.prices];
            const currentCountry = countries.find(c => c.name === countryName);

            // Check if all OTHER countries are 0 or empty
            const allOthersAreZero = countries.every(c => {
                if (c.name === countryName) return true;
                const pObj = f.prices.find(p => p.country === c.name);
                const amt = pObj ? pObj.amount : (f.price ? (Number(f.price) * (c.exchangeRate || c.rate || 1)) : '');
                return amt === '' || Number(amt) === 0;
            });

            if (allOthersAreZero) {
                if (numVal === '') {
                    const clearedPrices = countries.map(c => ({
                        country: c.name,
                        currency: c.currency || '',
                        amount: ''
                    }));
                    return { ...f, price: '', prices: clearedPrices };
                }

                const rate = currentCountry?.exchangeRate || currentCountry?.rate || 1;
                const baseValue = numVal / rate;

                const autoCalculatedPrices = countries.map(c => ({
                    country: c.name,
                    currency: c.currency || '',
                    amount: c.name === countryName ? numVal : Number((baseValue * (c.exchangeRate || c.rate || 1)).toFixed(2))
                }));

                return { ...f, price: Number(baseValue.toFixed(2)), prices: autoCalculatedPrices };
            }

            const idx = newPrices.findIndex(p => p.country === countryName);
            if (idx >= 0) {
                newPrices[idx] = { ...newPrices[idx], amount: numVal };
            } else {
                newPrices.push({
                    country: countryName,
                    currency: currentCountry?.currency || '',
                    amount: numVal
                });
            }
            return { ...f, prices: newPrices };
        });
    };

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Plan Name is required.', 'error'); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                isFree: form.isFree,
                price: form.price === '' ? 0 : Number(form.price),
                prices: form.prices || [],
                pricePeriod: form.pricePeriod.trim(),
                isAllCountries: form.isAllCountries,
                targetCountries: form.isAllCountries ? [] : form.targetCountries,
                fleetLimit: form.fleetLimit === '' ? 0 : Number(form.fleetLimit),
                driversLimit: form.driversLimit === '' ? 0 : Number(form.driversLimit),
                routesLimit: form.routesLimit === '' ? 0 : Number(form.routesLimit),
                usersLimit: form.usersLimit === '' ? 0 : Number(form.usersLimit),
                passengerLimit: form.passengerLimit === '' ? 0 : Number(form.passengerLimit),
                clientsLimit: form.clientsLimit === '' ? 0 : Number(form.clientsLimit),
                isActive: form.isActive,
            };
            console.log("Saving Price Plan Payload:", payload);
            const url = isEditMode
                ? `/api/price-plans/${editPlan._id}`
                : '/api/price-plans';
            const res = await fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (res.ok) {
                showToast(isEditMode ? 'Plan updated!' : 'Plan created successfully!', 'success');
                if (!isEditMode) setForm(defaultForm);
                if (onSaved) onSaved(data.plan);
            } else {
                showToast(data.message || 'Failed to save', 'error');
            }
        } catch {
            showToast('Server error. Is the backend running?', 'error');
        } finally {
            setSaving(false);
        }
    };

    const renderLimitField = (label, name) => {
        const isUnlimited = form[name] === -1;
        return (
            <div className="cpp-field quarter">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                    <label className="cpp-label" style={{ marginBottom: 0 }}>{label}<span className="cpp-required">*</span></label>
                    <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#555', fontWeight: 600 }}>
                        <input 
                            type="checkbox" 
                            checked={isUnlimited}
                            onChange={(e) => setForm(f => ({ ...f, [name]: e.target.checked ? -1 : '' }))}
                        /> Unlimited
                    </label>
                </div>
                <input
                    className="cpp-input"
                    type={isUnlimited ? 'text' : 'number'}
                    name={name}
                    value={isUnlimited ? 'Unlimited' : form[name]}
                    onChange={handleChange}
                    placeholder="Enter limit"
                    disabled={isUnlimited}
                    min="0"
                />
            </div>
        );
    };

    const handleCountryToggle = (countryName) => {
        setForm(f => {
            const current = new Set(f.targetCountries);
            if (current.has(countryName)) {
                current.delete(countryName);
            } else {
                current.add(countryName);
            }
            return { ...f, targetCountries: Array.from(current) };
        });
    };

    const applicableCountries = form.isAllCountries ? countries : countries.filter(c => (form.targetCountries || []).includes(c.name));

    return (
        <div className="cpp-container">
            {/* Toast */}
            {toast && (
                <div className={`pp-toast ${toast.type}`}>
                    {toast.msg}
                </div>
            )}

            <div className="cpp-header">
                <h1 className="cpp-title">{isEditMode ? 'Edit Plan' : 'Create Plan'}</h1>
            </div>

            <div className="cpp-form-card">
                <div className="cpp-row">
                    <div className="cpp-field full">
                        <label className="cpp-label">Plan Name<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Plan Name"
                        />
                    </div>
                </div>

                <div className="cpp-row">
                    <div className="cpp-field full">
                        <label className="cpp-label">Description</label>
                        <textarea
                            className="cpp-input cpp-textarea"
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Description"
                            rows={3}
                        />
                    </div>
                </div>

                <div className="cpp-row">
                    <div className="cpp-field quarter flex-row">
                        <input
                            type="checkbox"
                            className="cpp-checkbox"
                            id="isFree"
                            checked={form.isFree}
                            onChange={(e) => setForm(f => ({ ...f, isFree: e.target.checked }))}
                        />
                        <label htmlFor="isFree" className="cpp-checkbox-label">Is Free</label>
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Plan Price<span className="cpp-required">*</span></label>
                        <input
                            className="cpp-input"
                            type="number"
                            name="price"
                            value={form.price}
                            onChange={handleChange}
                            placeholder="Plan Price"
                            disabled={form.isFree}
                        />
                    </div>

                    <div className="cpp-field quarter">
                        <label className="cpp-label">Billing Period<span className="cpp-required">*</span></label>
                        <select
                            className="cpp-input"
                            name="pricePeriod"
                            value={form.pricePeriod}
                            onChange={handleChange}
                        >
                            <option value="">Select Period</option>
                            {billingPeriods.map(bp => (
                                <option key={bp._id} value={bp.name}>{bp.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Applicable Countries Section */}
                <div className="cpp-row" style={{ marginTop: 10 }}>
                    <div className="cpp-field full">
                        <label className="cpp-label">Applicable Countries<span className="cpp-required">*</span></label>
                        <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#334155' }}>
                                <input 
                                    type="radio" 
                                    name="isAllCountries" 
                                    checked={form.isAllCountries} 
                                    onChange={() => setForm(f => ({ ...f, isAllCountries: true }))}
                                    style={{ accentColor: '#3f51b5', width: '16px', height: '16px' }}
                                />
                                All Countries
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#334155' }}>
                                <input 
                                    type="radio" 
                                    name="isAllCountries" 
                                    checked={!form.isAllCountries} 
                                    onChange={() => setForm(f => ({ ...f, isAllCountries: false }))}
                                    style={{ accentColor: '#3f51b5', width: '16px', height: '16px' }}
                                />
                                Specific Countries
                            </label>
                        </div>

                        {!form.isAllCountries && (
                            <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                {countries.map(c => (
                                    <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', background: 'white', padding: '6px 12px', borderRadius: '20px', border: '1px solid #cbd5e1', cursor: 'pointer', userSelect: 'none' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={(form.targetCountries || []).includes(c.name)}
                                            onChange={() => handleCountryToggle(c.name)}
                                            style={{ accentColor: '#3f51b5' }}
                                        />
                                        {c.name}
                                    </label>
                                ))}
                                {countries.length === 0 && <span style={{ fontSize: '13px', color: '#94a3b8' }}>No countries available</span>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Localized Pricing Section */}
                {applicableCountries.length > 0 && !form.isFree && (
                    <div className="cpp-row" style={{ flexWrap: 'wrap', marginTop: 10, background: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ width: '100%', marginBottom: 15 }}>
                            <label className="cpp-label" style={{ fontSize: 14, fontWeight: 700, color: '#3f51b5' }}>Localized Pricing (Auto-calculated, modify as needed)</label>
                        </div>
                        {applicableCountries.map(c => {
                            const pObj = form.prices.find(p => p.country === c.name);
                            const amount = pObj ? pObj.amount : (form.price ? (Number(form.price) * (c.exchangeRate || c.rate || 1)) : '');
                            return (
                                <div className="cpp-field" style={{ width: 'calc(33.33% - 14px)', marginBottom: 15 }} key={c.name}>
                                    <label className="cpp-label" style={{ fontSize: 12 }}>{c.name} ({c.currency || ''})</label>
                                    <input
                                        className="cpp-input"
                                        type="number"
                                        value={amount}
                                        onChange={(e) => handlePriceChange(c.name, e.target.value)}
                                        placeholder={`Amount in ${c.currency || ''}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ width: '100%', marginTop: 30, marginBottom: 15 }}>
                    <label className="cpp-label" style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Plan Limits</label>
                </div>

                <div className="cpp-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                    {renderLimitField('Fleet Limit', 'fleetLimit')}
                    {renderLimitField('Drivers Limit', 'driversLimit')}
                    {renderLimitField('Routes Limit', 'routesLimit')}
                    {renderLimitField('Users Limit', 'usersLimit')}
                    {renderLimitField('Passenger Limit', 'passengerLimit')}
                    {renderLimitField('Clients Limit', 'clientsLimit')}
                </div>

                <div className="cpp-actions">
                    <button className="cpp-save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : (isEditMode ? 'Update Plan' : 'Create Plan')}
                    </button>
                </div>
            </div>

            <style>{`
                .cpp-container {
                    padding: 40px;
                    background: #f1f4f9;
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }
                .cpp-header { margin-bottom: 24px; }
                .cpp-title { font-size: 20px; font-weight: 600; color: #434a60; }
                
                .cpp-form-card {
                    background: white;
                    padding: 30px;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                
                .cpp-row {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .cpp-field { display: flex; flex-direction: column; width: 100%; }
                .cpp-field.full { flex: 1; }
                .cpp-field.quarter { flex: 1; min-width: 0; }
                .cpp-field.flex-row { flex-direction: row; align-items: center; gap: 10px; }
                
                .cpp-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #555;
                    margin-bottom: 8px;
                }
                .cpp-required { color: #e53e3e; margin-left: 2px; }
                
                .cpp-input {
                    padding: 10px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 13px;
                    color: #434a60;
                    outline: none;
                    transition: border-color 0.2s;
                }
                .cpp-input:focus { border-color: #3f51b5; }
                .cpp-input::placeholder { color: #a0aec0; }
                
                .cpp-textarea { resize: vertical; min-height: 80px; }
                
                .cpp-checkbox {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                }
                .cpp-checkbox-label {
                    font-size: 13px;
                    color: #434a60;
                    cursor: pointer;
                }
                
                .cpp-hint {
                    font-size: 11px;
                    color: #718096;
                    margin-top: 4px;
                    font-style: italic;
                }
                
                .cpp-actions {
                    margin-top: 30px;
                    display: flex;
                    justify-content: flex-end;
                }
                .cpp-save-btn {
                    background: #3f51b5;
                    color: white;
                    border: none;
                    padding: 10px 30px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .cpp-save-btn:hover { background: #303f9f; }
                .cpp-save-btn:disabled { background: #a0aec0; cursor: not-allowed; }

                .pp-toast {
                    position: fixed; top: 20px; right: 20px;
                    padding: 12px 24px; border-radius: 8px;
                    background: #333; color: white;
                    z-index: 1000;
                }
                .pp-toast.success { background: #10b981; }
                .pp-toast.error { background: #e53e3e; }

                /* --- Dark Mode Overrides --- */
                .theme-dark .cpp-container {
                    background: #0f0f0f;
                }
                .theme-dark .cpp-title {
                    color: #e2e8f0;
                }
                .theme-dark .cpp-form-card {
                    background: #161616;
                    border: 1px solid #222;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
                }
                .theme-dark .cpp-label {
                    color: #888;
                }
                .theme-dark .cpp-checkbox-label {
                    color: #ccc;
                }
                .theme-dark .cpp-input {
                    background: #111;
                    border-color: #2a2a2a;
                    color: #ddd;
                }
                .theme-dark .cpp-input:focus {
                    border-color: #3f51b5;
                }
                .theme-dark .cpp-input::placeholder {
                    color: #444;
                }
                .theme-dark .cpp-hint {
                    color: #555;
                }
            `}</style>
        </div>
    );
}
